/**
 * BattleUI — React overlay that sits on top of the Phaser Battle scene.
 * Renders the question cards. Correct/wrong answers are communicated
 * back to Phaser via EventBus.
 *
 * Supported question types:
 *   multiple_choice    — pick one
 *   evidence_selection — pick N best pieces of evidence
 *   cause_effect       — click-to-match causes → effects
 *   drag_drop          — click-to-match sources → targets (same engine as cause_effect)
 *   timeline           — click to arrange items in chronological order
 *   short_response     — free-text (auto-passes with ≥10 chars)
 */
import { useState, useCallback } from 'react'
import { EventBus, EVENTS } from '@/game/EventBus'
import { useGame, TEXT_SCALE_LABELS } from '@/context/GameContext'
import type { Standard, Question } from '@/api/client'

interface Props {
  standard: Standard
  questions: Question[]
  onComplete: (result: { score: number; max_score: number; items: unknown[]; victory: boolean }) => void
  onRetreat: () => void
  textScale?: number
}

interface ItemResult {
  question_id: number
  correct: boolean
  response: unknown
  points_earned: number
}

export default function BattleUI({ standard, questions, onComplete, onRetreat, textScale: scaleProp }: Props) {
  const { textScale: ctxScale, textScaleIndex, cycleTextScale } = useGame()
  // Prop wins if provided (forwarded from GameWorld); otherwise use context
  const s = scaleProp ?? ctxScale

  const [qIndex, setQIndex]     = useState(0)
  const [results, setResults]   = useState<ItemResult[]>([])
  const [selected, setSelected] = useState<unknown>(null)
  const [revealed, setRevealed] = useState(false)
  const [isCorrect, setIsCorrect] = useState(false)

  const current = questions[qIndex]
  const totalPoints = questions.reduce((s, q) => s + q.points, 0)

  const checkAnswer = useCallback(() => {
    if (!current || !hasSelection(current, selected)) return

    const correct = isAnswerCorrect(current, selected)
    setIsCorrect(correct)
    setRevealed(true)

    if (correct) {
      EventBus.emit(EVENTS.ANSWER_CORRECT, { damage: current.points })
    } else {
      EventBus.emit(EVENTS.ANSWER_WRONG)
    }
  }, [current, selected])

  const nextQuestion = useCallback(() => {
    if (!current) return

    const correct = isAnswerCorrect(current, selected)
    const newResults = [...results, {
      question_id: current.id,
      correct,
      response: selected,
      points_earned: correct ? current.points : 0,
    }]
    setResults(newResults)
    setSelected(null)
    setRevealed(false)
    setIsCorrect(false)

    if (qIndex + 1 >= questions.length) {
      const score = newResults.reduce((s, r) => s + r.points_earned, 0)
      const victory = score / totalPoints >= 0.5
      onComplete({ score, max_score: totalPoints, items: newResults, victory })
    } else {
      setQIndex(i => i + 1)
    }
  }, [current, selected, results, qIndex, questions.length, totalPoints, onComplete])

  if (!current) return null

  const opts = current.options_json as any
  const progress = (qIndex / questions.length) * 100
  const canAttack = hasSelection(current, selected)

  return (
    <div className="flex flex-col" style={{ height: '100%', background: 'transparent' }}>
      {/* Spacer — battle scene shows through here; capped so card has room on small screens */}
      <div style={{ height: 'min(28vh, 130px)', flexShrink: 0 }} />

      <div className="mx-auto w-full max-w-2xl px-4" style={{ paddingBottom: 0 }}>
        {/* Progress bar */}
        <div className="mb-2 flex items-center gap-2">
          <div className="flex-1 h-2 rounded-full" style={{ background: '#1e1b4b' }}>
            <div className="h-2 rounded-full transition-all duration-500"
                 style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #6366f1, #a78bfa)' }} />
          </div>
          <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${7 * s}px`, color: '#818cf8' }}>
            {qIndex + 1}/{questions.length}
          </span>
        </div>

        {/* Question card: flex column so action footer stays pinned below scrollable content */}
        <div className="rounded-xl" style={{
          background: 'rgba(10,10,35,0.97)',
          border: `2px solid ${revealed ? (isCorrect ? '#34d399' : '#ef4444') : '#4338ca'}`,
          boxShadow: '0 -4px 40px rgba(99,102,241,0.3)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100dvh - min(28vh, 130px) - 52px)',
          overflow: 'hidden',
        }}>
          {/* Scrollable region — question + answer options + feedback */}
          <div style={{
            overflowY: 'auto',
            flex: '1 1 auto',
            minHeight: 0,
            padding: '1rem',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            touchAction: 'pan-y',
          }}>
          {/* Standard tag + accessibility controls + retreat */}
          <div className="flex items-center gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
            <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${6 * s}px`,
                           color: '#a78bfa', background: '#1e1b4b',
                           border: '1px solid #312e81', padding: '2px 6px', borderRadius: 4 }}>
              {standard.standard_id}
            </span>
            <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${6 * s}px`, color: '#6b7280' }}>
              {current.difficulty === 1 ? '⭐ Easy' : current.difficulty === 2 ? '⭐⭐ Medium' : '⭐⭐⭐ Hard'}
            </span>
            <span className="ml-auto" style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${6 * s}px`, color: '#fbbf24' }}>
              {current.points} pts
            </span>
            {/* Text-size toggle — cycles Normal → Large → X-Large */}
            <button
              onClick={cycleTextScale}
              title={`Text size: ${TEXT_SCALE_LABELS[textScaleIndex]} (click to change)`}
              style={{
                fontFamily: 'system-ui', fontSize: `${11 * s}px`, fontWeight: 700,
                color: textScaleIndex === 0 ? '#6b7280' : '#fbbf24',
                background: textScaleIndex === 0 ? 'none' : 'rgba(251,191,36,0.1)',
                border: `1px solid ${textScaleIndex === 0 ? '#374151' : '#fbbf24'}`,
                borderRadius: 4, padding: '2px 7px', cursor: 'pointer',
                lineHeight: 1,
              }}>
              Aa
            </button>
            <button
              onClick={onRetreat}
              title="Retreat to world map"
              style={{
                fontFamily: '"Press Start 2P", monospace', fontSize: `${6 * s}px`,
                color: '#6b7280', background: 'none',
                border: '1px solid #374151', borderRadius: 4,
                padding: '2px 6px', cursor: 'pointer',
              }}>
              🏃 RETREAT
            </button>
          </div>

          {/* Scenario (optional) */}
          {current.scenario && (
            <div className="mb-3 p-3 rounded" style={{ background: '#0d0d26', border: '1px solid #312e81' }}>
              <p style={{ fontFamily: 'Georgia, serif', fontSize: `${12 * s}px`, color: '#94a3b8', lineHeight: 1.6 }}>
                {current.scenario}
              </p>
            </div>
          )}

          {/* Prompt */}
          <p className="mb-4" style={{ fontFamily: 'system-ui, sans-serif', fontSize: `${14 * s}px`,
                                       color: '#e0e7ff', lineHeight: 1.5, fontWeight: 500 }}>
            {current.prompt}
          </p>

          {/* ── Answer options by type ───────────────────────────────────────── */}
          {current.type === 'multiple_choice' && opts && (
            <MultipleChoiceOptions
              options={opts}
              selected={selected as string | null}
              revealed={revealed}
              correct={current.correct_json as string}
              onSelect={setSelected}
              scale={s}
            />
          )}

          {current.type === 'evidence_selection' && opts && (
            <EvidenceSelectionOptions
              options={opts}
              selected={(selected as string[]) || []}
              revealed={revealed}
              correct={current.correct_json as string[]}
              onSelect={setSelected}
              scale={s}
            />
          )}

          {(current.type === 'drag_drop' || current.type === 'cause_effect') && opts && (
            <MatchingOptions
              options={opts}
              value={(selected as Record<string, string>) || {}}
              revealed={revealed}
              correct={current.correct_json as Record<string, string>}
              onChange={setSelected}
              leftLabel={current.type === 'cause_effect' ? 'CAUSES' : 'ITEMS'}
              rightLabel={current.type === 'cause_effect' ? 'EFFECTS' : 'MATCHES'}
              scale={s}
            />
          )}

          {current.type === 'timeline' && opts && (
            <TimelineOptions
              options={opts as Array<{ key: string; text: string }>}
              value={(selected as string[]) || []}
              revealed={revealed}
              correct={current.correct_json as string[]}
              onChange={setSelected}
              scale={s}
            />
          )}

          {current.type === 'short_response' && (
            <textarea
              value={(selected as string) || ''}
              onChange={e => setSelected(e.target.value)}
              placeholder="Write your answer here..."
              rows={3}
              className="w-full p-2 rounded outline-none resize-none"
              style={{ fontFamily: 'system-ui', fontSize: `${13 * s}px`,
                       background: '#1e1b4b', border: '1px solid #4338ca', color: '#e0e7ff' }}
            />
          )}

          {/* Hint / feedback */}
          {revealed && (
            <div className="mt-3 p-3 rounded" style={{
              background: isCorrect ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${isCorrect ? '#34d399' : '#ef4444'}`,
            }}>
              <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${8 * s}px`,
                          color: isCorrect ? '#34d399' : '#ef4444', marginBottom: 4 }}>
                {isCorrect ? '✅ CORRECT!' : '❌ INCORRECT'}
              </p>
              {current.hint && (
                <p style={{ fontFamily: 'system-ui', fontSize: `${12 * s}px`, color: '#94a3b8' }}>
                  💡 {current.hint}
                </p>
              )}
            </div>
          )}

          </div>{/* ── end scrollable region ── */}

          {/* Sticky action footer — always visible regardless of card content height */}
          <div style={{
            flexShrink: 0,
            padding: '0.5rem 1rem max(0.75rem, env(safe-area-inset-bottom, 0.75rem))',
            borderTop: '1px solid rgba(67,56,202,0.35)',
            background: 'rgba(10,10,35,0.97)',
          }}>
          {/* Action buttons */}
          <div className="flex gap-3">
            {!revealed ? (
              <button
                onClick={checkAnswer}
                disabled={!canAttack}
                className="flex-1 py-2 rounded transition-all"
                style={{
                  fontFamily: '"Press Start 2P", monospace', fontSize: `${9 * s}px`,
                  background: '#4f46e5', color: '#ffffff', border: '2px solid #818cf8',
                  cursor: canAttack ? 'pointer' : 'not-allowed',
                  opacity: canAttack ? 1 : 0.5,
                }}>
                ⚔️ ATTACK
              </button>
            ) : (
              <button
                onClick={nextQuestion}
                className="flex-1 py-2 rounded transition-all hover:scale-105"
                style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${9 * s}px`,
                         background: isCorrect ? '#065f46' : '#7f1d1d',
                         color: '#ffffff', border: `2px solid ${isCorrect ? '#34d399' : '#ef4444'}`,
                         cursor: 'pointer' }}>
                {qIndex + 1 < questions.length ? '▶ NEXT' : isCorrect ? '🏆 FINISH' : '🔄 CONTINUE'}
              </button>
            )}
          </div>{/* ── end action buttons ── */}
          </div>{/* ── end sticky action footer ── */}
        </div>{/* ── end question card ── */}
      </div>
    </div>
  )
}

// ── Multiple Choice ───────────────────────────────────────────────────────────

function MultipleChoiceOptions({ options, selected, revealed, correct, onSelect, scale = 1 }: {
  options: Array<{ key: string; text: string }>
  selected: string | null
  revealed: boolean
  correct: string
  onSelect: (v: string) => void
  scale?: number
}) {
  return (
    <div className="grid grid-cols-1 gap-2">
      {options.map(opt => {
        const isSelected = selected === opt.key
        const isCorrectAnswer = opt.key === correct
        let borderColor = '#312e81'
        if (revealed && isCorrectAnswer)  borderColor = '#34d399'
        if (revealed && isSelected && !isCorrectAnswer) borderColor = '#ef4444'
        if (!revealed && isSelected) borderColor = '#818cf8'

        return (
          <button key={opt.key}
            onClick={() => !revealed && onSelect(opt.key)}
            className="text-left px-3 py-2 rounded transition-all"
            style={{
              background: revealed && isCorrectAnswer ? 'rgba(16,185,129,0.15)'
                        : isSelected ? '#1e1b4b' : '#0d0d26',
              border: `2px solid ${borderColor}`,
              color: '#e0e7ff', fontFamily: 'system-ui', fontSize: `${13 * scale}px`,
              cursor: revealed ? 'default' : 'pointer',
            }}>
            <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${8 * scale}px`,
                           color: '#818cf8', marginRight: 8 }}>
              [{opt.key}]
            </span>
            {opt.text}
          </button>
        )
      })}
    </div>
  )
}

// ── Evidence Selection ────────────────────────────────────────────────────────

function EvidenceSelectionOptions({ options, selected, revealed, correct, onSelect, scale = 1 }: {
  options: Array<{ key: string; text: string }>
  selected: string[]
  revealed: boolean
  correct: string[]
  onSelect: (v: string[]) => void
  scale?: number
}) {
  function toggle(key: string) {
    if (revealed) return
    const next = selected.includes(key)
      ? selected.filter(k => k !== key)
      : [...selected, key]
    onSelect(next)
  }

  return (
    <div className="space-y-2">
      <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${6 * scale}px`, color: '#6b7280', marginBottom: 8 }}>
        Select the BEST {correct.length} pieces of evidence:
      </p>
      {options.map(opt => {
        const isSelected = selected.includes(opt.key)
        const isCorrectAnswer = correct.includes(opt.key)
        let bg = '#0d0d26'
        let border = '#312e81'
        if (revealed && isCorrectAnswer)  { bg = 'rgba(16,185,129,0.15)'; border = '#34d399' }
        if (revealed && isSelected && !isCorrectAnswer) { bg = 'rgba(239,68,68,0.1)'; border = '#ef4444' }
        if (!revealed && isSelected)      { bg = '#1e1b4b'; border = '#818cf8' }

        return (
          <button key={opt.key}
            onClick={() => toggle(opt.key)}
            className="w-full text-left px-3 py-2 rounded flex items-start gap-2"
            style={{ background: bg, border: `2px solid ${border}`,
                     color: '#e0e7ff', cursor: revealed ? 'default' : 'pointer',
                     fontFamily: 'system-ui', fontSize: `${12 * scale}px` }}>
            <span style={{ marginTop: 1, flexShrink: 0, fontFamily: '"Press Start 2P", monospace',
                           fontSize: `${9 * scale}px`, color: '#818cf8' }}>
              {isSelected ? '☑' : '☐'}
            </span>
            {opt.text}
          </button>
        )
      })}
    </div>
  )
}

// ── Matching (cause_effect + drag_drop) — click-based ────────────────────────
// Click a left item to "pick it up", then click a right slot to assign it.
// Click an occupied slot to unassign. Click [✕ cancel] to deselect.

function MatchingOptions({ options: rawOptions, value, revealed, correct, onChange, leftLabel, rightLabel, scale = 1 }: {
  options: Record<string, Array<{ key: string; text: string }>>
  value: Record<string, string>       // sourceKey → targetKey
  revealed: boolean
  correct: Record<string, string>
  onChange: (v: Record<string, string>) => void
  leftLabel: string
  rightLabel: string
  scale?: number
}) {
  // Normalize: cause_effect uses {causes, effects}; drag_drop uses {sources, targets}
  const options = {
    sources: rawOptions.sources ?? rawOptions.causes ?? [],
    targets: rawOptions.targets ?? rawOptions.effects ?? [],
  }
  // picked is stored as a string; source keys may be numbers from the DD helper so we normalise
  const [picked, setPicked] = useState<string | null>(null)

  function pickSource(srcKey: unknown) {
    if (revealed) return
    setPicked(String(srcKey))
  }

  function cancelPick() {
    setPicked(null)
  }

  function assignToTarget(tgtKey: string) {
    if (revealed) return

    if (picked !== null) {
      // Assign picked source to this target (remove any previous assignment to this target)
      const next = { ...value }
      for (const k of Object.keys(next)) {
        if (next[k] === tgtKey) delete next[k]
      }
      next[String(picked)] = tgtKey
      onChange(next)
      setPicked(null)
    } else {
      // If target already has a source, un-assign it
      const existing = Object.entries(value).find(([, v]) => v === tgtKey)
      if (existing) {
        const next = { ...value }
        delete next[existing[0]]
        onChange(next)
      }
    }
  }

  // Reverse lookup: targetKey → sourceKey
  const targetToSource: Record<string, string> = {}
  for (const [src, tgt] of Object.entries(value)) targetToSource[tgt] = src

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${6 * scale}px`, color: '#a78bfa', margin: 0 }}>
          {picked !== null
            ? '👆 Now click a target on the right →'
            : `Click a ${leftLabel.toLowerCase().slice(0, -1)} on the left, then match it right`}
        </p>
        {picked !== null && !revealed && (
          <button
            onClick={cancelPick}
            style={{
              fontFamily: '"Press Start 2P", monospace', fontSize: `${6 * scale}px`,
              color: '#ef4444', background: 'rgba(239,68,68,0.1)',
              border: '1px solid #ef4444', borderRadius: 4,
              padding: '2px 6px', cursor: 'pointer', flexShrink: 0,
            }}>
            ✕ cancel
          </button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* Left column: sources */}
        <div>
          <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${6 * scale}px`, color: '#6b7280', marginBottom: 6 }}>
            {leftLabel}:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {options.sources.map(src => {
              const srcKey     = String(src.key)
              const isAssigned = srcKey in value
              const isPicked   = picked === srcKey
              let bg     = isAssigned ? '#0d0d1a' : '#1e1b4b'
              let border = isAssigned ? '#1e1b4b' : '#4338ca'
              if (isPicked) { bg = '#312e81'; border = '#a78bfa' }
              if (revealed) {
                const ok = value[src.key] === correct[src.key]
                bg     = ok ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.1)'
                border = ok ? '#34d399' : '#ef4444'
              }

              return (
                <button key={srcKey}
                  type="button"
                  onClick={() => pickSource(srcKey)}
                  // Disable when revealed, already assigned, OR currently picked (prevents accidental un-pick)
                  disabled={revealed || isAssigned || isPicked}
                  style={{
                    background: bg, border: `2px solid ${border}`,
                    color: isAssigned && !revealed ? '#374151' : '#e0e7ff',
                    fontFamily: 'system-ui', fontSize: `${11 * scale}px`,
                    padding: '6px 10px', borderRadius: 6, textAlign: 'left',
                    cursor: (revealed || isAssigned || isPicked) ? 'default' : 'pointer',
                    opacity: isAssigned && !revealed ? 0.5 : 1,
                    transition: 'all 0.15s',
                    boxShadow: isPicked ? '0 0 0 3px rgba(167,139,250,0.4)' : 'none',
                  }}>
                  {isPicked && <span style={{ marginRight: 4 }}>👆</span>}
                  {src.text}
                </button>
              )
            })}
          </div>
        </div>

        {/* Right column: targets — use <button> for reliable click handling */}
        <div>
          <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${6 * scale}px`, color: '#6b7280', marginBottom: 6 }}>
            {rightLabel}:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {options.targets.map(tgt => {
              const assignedSrcKey = targetToSource[tgt.key]
              const assignedSrc    = assignedSrcKey
                ? options.sources.find(s => String(s.key) === assignedSrcKey)
                : null
              const isHighlighted  = picked !== null && !assignedSrcKey

              let bg     = '#0d0d26'
              let border = '#312e81'
              if (assignedSrc)   { bg = '#1e1b4b'; border = '#6366f1' }
              if (isHighlighted) { bg = 'rgba(167,139,250,0.07)'; border = '#a78bfa' }
              if (revealed) {
                const ok = assignedSrcKey && correct[assignedSrcKey] === tgt.key
                bg     = ok ? 'rgba(16,185,129,0.1)' : assignedSrcKey ? 'rgba(239,68,68,0.1)' : '#0d0d26'
                border = ok ? '#34d399' : assignedSrcKey ? '#ef4444' : '#312e81'
              }

              return (
                <button key={tgt.key}
                  type="button"
                  onClick={() => assignToTarget(tgt.key)}
                  disabled={revealed}
                  style={{
                    background: bg, border: `2px dashed ${border}`,
                    color: '#94a3b8', fontFamily: 'system-ui', fontSize: `${11 * scale}px`,
                    padding: '6px 10px', borderRadius: 6, minHeight: 38,
                    width: '100%', textAlign: 'left',
                    cursor: revealed ? 'default' : 'pointer',
                    transition: 'all 0.15s',
                    boxShadow: isHighlighted ? '0 0 0 3px rgba(167,139,250,0.2)' : 'none',
                  }}>
                  <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${6 * scale}px`,
                                 color: '#6b7280', display: 'block', marginBottom: 3 }}>
                    {tgt.text}
                  </span>
                  {assignedSrc && (
                    <span style={{ color: revealed
                      ? (correct[assignedSrcKey!] === tgt.key ? '#34d399' : '#ef4444')
                      : '#e0e7ff', fontSize: `${11 * scale}px` }}>
                      → {assignedSrc.text}
                      {!revealed && (
                        <span style={{ color: '#6b7280', marginLeft: 4, fontSize: `${9 * scale}px` }}>(click to remove)</span>
                      )}
                    </span>
                  )}
                  {!assignedSrc && isHighlighted && (
                    <span style={{ color: '#a78bfa', fontSize: `${11 * scale}px` }}>Assign here ✓</span>
                  )}
                  {!assignedSrc && !isHighlighted && (
                    <span style={{ color: '#374151', fontSize: `${11 * scale}px` }}>—</span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Timeline — click-to-arrange ───────────────────────────────────────────────
// Items start unordered in the left pool. Click an item to select it,
// then click a numbered slot to place it. Click an occupied slot to clear it.

function TimelineOptions({ options, value, revealed, correct, onChange, scale = 1 }: {
  options: Array<{ key: string; text: string }>
  value: string[]                  // ordered keys selected so far
  revealed: boolean
  correct: string[]
  onChange: (v: string[]) => void
  scale?: number
}) {
  const [picked, setPicked] = useState<string | null>(null)

  // value is a sparse array? No — let's use a simpler model:
  // value = ordered array of placed keys (length ≤ options.length)
  // unplaced = options not yet in value

  const placedSet = new Set(value)
  const unplaced  = options.filter(o => !placedSet.has(o.key))

  function pickUnplaced(key: string) {
    if (revealed) return
    setPicked(p => p === key ? null : key)
  }

  function placeAtSlot(slotIdx: number) {
    if (revealed) return
    const next = [...value]

    if (next[slotIdx] !== undefined) {
      // Slot occupied: unplace the existing item (send back to pool)
      next.splice(slotIdx, 1)
      onChange(next)
      return
    }

    if (picked) {
      // Extend array with null placeholders if needed
      while (next.length < slotIdx) next.push('')
      next.splice(slotIdx, 0, picked)
      onChange(next)
      setPicked(null)
    }
  }

  function removeFromSlot(slotIdx: number) {
    if (revealed) return
    const next = [...value]
    next.splice(slotIdx, 1)
    onChange(next)
  }

  const totalSlots = options.length

  return (
    <div>
      <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${6 * scale}px`, color: '#a78bfa', marginBottom: 8 }}>
        {picked ? '👆 Click a numbered slot to place it' : 'Click an event, then click its position in history'}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* Left: unplaced pool */}
        <div>
          <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${6 * scale}px`, color: '#6b7280', marginBottom: 6 }}>
            EVENTS (click to pick):
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {unplaced.map(opt => {
              const isPicked = picked === opt.key
              return (
                <button key={opt.key}
                  onClick={() => pickUnplaced(opt.key)}
                  style={{
                    background: isPicked ? '#312e81' : '#1e1b4b',
                    border: `2px solid ${isPicked ? '#a78bfa' : '#4338ca'}`,
                    color: '#e0e7ff', fontFamily: 'system-ui', fontSize: `${11 * scale}px`,
                    padding: '6px 10px', borderRadius: 6, textAlign: 'left',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}>
                  {isPicked && <span style={{ marginRight: 4 }}>👆</span>}
                  {opt.text}
                </button>
              )
            })}
            {unplaced.length === 0 && (
              <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${6 * scale}px`, color: '#34d399' }}>
                ✅ All placed!
              </p>
            )}
          </div>
        </div>

        {/* Right: numbered slots */}
        <div>
          <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${6 * scale}px`, color: '#6b7280', marginBottom: 6 }}>
            TIMELINE ORDER:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {Array.from({ length: totalSlots }, (_, i) => {
              const placedKey  = value[i]
              const placedOpt  = placedKey ? options.find(o => o.key === placedKey) : null
              const isCorrectSlot = revealed && placedKey && correct[i] === placedKey

              let bg     = '#0d0d26'
              let border = '#312e81'
              if (placedOpt && !revealed) { bg = '#1e1b4b'; border = '#6366f1' }
              if (!!picked && !placedOpt) { border = '#a78bfa' }
              if (revealed) {
                bg     = isCorrectSlot ? 'rgba(16,185,129,0.1)' : placedOpt ? 'rgba(239,68,68,0.1)' : '#0d0d26'
                border = isCorrectSlot ? '#34d399' : placedOpt ? '#ef4444' : '#312e81'
              }

              return (
                <div key={i}
                  onClick={() => placeAtSlot(i)}
                  style={{
                    background: bg, border: `2px dashed ${border}`,
                    color: '#94a3b8', fontFamily: 'system-ui', fontSize: `${11 * scale}px`,
                    padding: '6px 10px', borderRadius: 6, minHeight: 38,
                    cursor: revealed ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    transition: 'all 0.15s',
                  }}>
                  <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: `${7 * scale}px`,
                                 color: '#818cf8', flexShrink: 0, marginTop: 2 }}>
                    {i + 1}.
                  </span>
                  <div style={{ flex: 1 }}>
                    {placedOpt ? (
                      <span style={{ color: revealed
                        ? (isCorrectSlot ? '#34d399' : '#ef4444')
                        : '#e0e7ff', fontSize: `${11 * scale}px` }}>
                        {placedOpt.text}
                        {!revealed && (
                          <button
                            onClick={e => { e.stopPropagation(); removeFromSlot(i) }}
                            style={{ marginLeft: 6, color: '#6b7280', background: 'none',
                                     border: 'none', cursor: 'pointer', fontSize: `${10 * scale}px` }}>
                            ✕
                          </button>
                        )}
                      </span>
                    ) : (
                      <span style={{ color: picked ? '#a78bfa' : '#374151', fontSize: `${10 * scale}px` }}>
                        {picked ? 'Place here…' : '—'}
                      </span>
                    )}
                    {revealed && !placedOpt && (
                      <span style={{ color: '#ef4444', fontSize: `${10 * scale}px` }}>
                        Should be: {options.find(o => o.key === correct[i])?.text || '?'}
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Returns true if the student has made a valid selection for this question type */
function hasSelection(q: Question, selected: unknown): boolean {
  if (selected === null || selected === undefined) return false
  switch (q.type) {
    case 'multiple_choice':
      return typeof selected === 'string' && selected.length > 0
    case 'evidence_selection':
      return Array.isArray(selected) && selected.length > 0
    case 'drag_drop':
    case 'cause_effect': {
      const v = selected as Record<string, string>
      const opts = q.options_json as Record<string, Array<{ key: unknown }>>
      const sources = opts?.sources ?? opts?.causes ?? []
      const targets = opts?.targets ?? opts?.effects ?? []
      // Require all pairs to be filled (capped at whichever side is smaller)
      const required = Math.min(sources.length, targets.length)
      return required > 0 ? Object.keys(v).length >= required : Object.keys(v).length > 0
    }
    case 'timeline': {
      const opts = q.options_json as Array<{ key: string }>
      return Array.isArray(selected) && selected.length >= opts.length
    }
    case 'short_response':
      return typeof selected === 'string' && selected.trim().length >= 10
    default:
      return false
  }
}

function isAnswerCorrect(q: Question, selected: unknown): boolean {
  const correct = q.correct_json
  if (!selected) return false

  switch (q.type) {
    case 'multiple_choice':
      return selected === correct

    case 'evidence_selection': {
      const sel = (selected as string[]).sort().join(',')
      const exp = (correct as string[]).sort().join(',')
      return sel === exp
    }

    case 'drag_drop':
    case 'cause_effect': {
      const sel = selected as Record<string, string>
      // Normalise correct keys to strings (DD helper produces numeric keys like {0:'T0'})
      const exp = correct as Record<string | number, string>
      return Object.keys(exp).every(k => sel[String(k)] === exp[k])
    }

    case 'timeline': {
      const sel = (selected as string[]).join(',')
      const exp = (correct as string[]).join(',')
      return sel === exp
    }

    case 'short_response':
      return typeof selected === 'string' && selected.trim().length >= 10

    default:
      return false
  }
}
